import React, { useState, useRef, useEffect, ReactNode } from 'react';
import './swipeable-drawer.scss';

interface SwipeableDrawerProps {
  children: ReactNode | ReactNode[];
  anchorRef: any;
  open: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  mobileBreakpoint?: number;
}

export const SwipeableDrawer: React.FC<SwipeableDrawerProps> = ({
  children,
  anchorRef,
  open,
  openDrawer,
  closeDrawer,
  mobileBreakpoint = 600,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [isMobile, setIsMobile] = useState(
    window.innerWidth < mobileBreakpoint
  );
  const [anchorPosition, setAnchorPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const drawerRef = useRef(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    setIsDragging(true);
    setStartY(e.touches[0].clientY);
    setCurrentY(e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isMobile || !isDragging) return;
    setCurrentY(e.touches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (!isMobile || !isDragging) return;
    setIsDragging(false);
    const diff = currentY - startY;
    if (diff > 100) closeDrawer();
    else if (diff < -50) openDrawer();
    setCurrentY(0);
    setStartY(0);
  };

  const getTransform = () => {
    if (!isMobile || !isDragging) return 0;
    const diff = currentY - startY;
    return Math.max(0, diff);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < mobileBreakpoint);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileBreakpoint]);

  useEffect(() => {
    if (!isMobile && anchorRef?.current) {
      const updatePosition = () => {
        let element = anchorRef.current;

        if (element && !element.getBoundingClientRect) {
          element =
            element.inputRef?.current ||
            element.ref?.current ||
            element.querySelector?.('input') ||
            element.querySelector?.('[role="textbox"]') ||
            element;
        }

        if (element && element.getBoundingClientRect) {
          const rect = element.getBoundingClientRect();
          setAnchorPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
          });
        }
      };

      const timeoutId = setTimeout(updatePosition, 0);
      updatePosition();

      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition);

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition);
      };
    }
    return () => {};
  }, [isMobile, anchorRef, open]);

  if (!isMobile && !open) return null;

  if (!isMobile && anchorPosition.width === 0) return null;

  return (
    <div
      ref={drawerRef}
      className={`drawer ${isMobile ? 'drawer-mobile' : 'drawer-desktop'} ${
        open ? 'open' : 'closed'
      } ${isDragging ? 'dragging' : ''}`}
      style={
        !isMobile
          ? {
              top: `${anchorPosition.top}px`,
              left: `${anchorPosition.left}px`,
              width: `${anchorPosition.width}px`,
            }
          : {
              transform: open
                ? `translateY(${getTransform()}px)`
                : 'translateY(calc(100%))',
            }
      }
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {isMobile && <div className="drawer-handle" />}
      <div className="drawer-content">{children}</div>
    </div>
  );
};
